import type { Theme } from '@emotion/react';
import styled from '@emotion/styled';

import type { BadgeColor, BadgeRoundness, BadgeSize, BadgeVariant } from './types';

type VariantColorConfig = {
  backgroundColor: string;
  color: string;
};

function getColorStyles(
  theme: Theme,
  color: BadgeColor,
  variant: BadgeVariant,
): VariantColorConfig {
  // Neutral and disabled read the same in both variants, as in the Figma `badge` set.
  if (color === 'default') {
    return { backgroundColor: theme.colors.background.overlay, color: theme.colors.text.primary };
  }

  if (color === 'disabled') {
    return { backgroundColor: theme.colors.background.raised, color: theme.colors.text.tertiary };
  }

  const configs: Record<
    Exclude<BadgeColor, 'default' | 'disabled'>,
    Record<BadgeVariant, VariantColorConfig>
  > = {
    primary: {
      solid: { backgroundColor: theme.colors.brand.solid, color: theme.colors.brand.onBrand },
      subtle: { backgroundColor: theme.colors.brand.subtle, color: theme.colors.brand.text },
    },
    secondary: {
      solid: { backgroundColor: theme.colors.info.solid, color: theme.colors.info.onInfo },
      subtle: { backgroundColor: theme.colors.info.subtle, color: theme.colors.info.text },
    },
    yellow: {
      solid: { backgroundColor: theme.colors.warning.solid, color: theme.colors.warning.onWarning },
      subtle: { backgroundColor: theme.colors.warning.subtle, color: theme.colors.warning.text },
    },
    orange: {
      solid: { backgroundColor: theme.colors.orange.solid, color: theme.colors.orange.onOrange },
      subtle: { backgroundColor: theme.colors.orange.subtle, color: theme.colors.orange.text },
    },
    purple: {
      solid: { backgroundColor: theme.colors.accent.solid, color: theme.colors.accent.onAccent },
      subtle: { backgroundColor: theme.colors.accent.subtle, color: theme.colors.accent.text },
    },
    destructive: {
      solid: { backgroundColor: theme.colors.error.solid, color: theme.colors.error.onError },
      subtle: { backgroundColor: theme.colors.error.subtle, color: theme.colors.error.text },
    },
  };

  return configs[color][variant];
}

const SIZE_CONFIG: Record<BadgeSize, { fontSize: number; lineHeight: string; iconSize: number }> = {
  sm: { fontSize: 12, lineHeight: '16px', iconSize: 12 },
  md: { fontSize: 14, lineHeight: '20px', iconSize: 16 },
  lg: { fontSize: 16, lineHeight: '24px', iconSize: 16 },
};

const TRANSIENT_PROPS = new Set(['$color', '$variant', '$size', '$roundness']);

export const StyledBadge = styled('span', {
  shouldForwardProp: (prop) => !TRANSIENT_PROPS.has(prop),
})<{
  $color: BadgeColor;
  $variant: BadgeVariant;
  $size: BadgeSize;
  $roundness: BadgeRoundness;
}>(({ theme, $color, $variant, $size, $roundness }) => {
  const colorStyles = getColorStyles(theme, $color, $variant);
  const { fontSize, lineHeight, iconSize } = SIZE_CONFIG[$size];
  const borderRadius = $roundness === 'round' ? 9999 : 8;

  return {
    width: 'fit-content',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    padding: '2px 8px',
    borderRadius,
    ...colorStyles,
    fontFamily: 'DM Sans, sans-serif',
    fontSize,
    fontWeight: 500,
    lineHeight,
    whiteSpace: 'nowrap',
    userSelect: 'none',
    boxSizing: 'border-box',

    svg: {
      width: iconSize,
      height: iconSize,
      flexShrink: 0,
    },
  };
});
